import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { Fragment, type Node as PMNode, Slice } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { Transform } from '@atlaskit/editor-prosemirror/transform';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type {
	MoveNodeCommand,
	StreamingCommandFactory,
} from '../aiStreamingOrchestratorPluginType';
import { findNodeByLocalId, findNodeByLocalIdAndDoc } from './utils/find-node-by-local-id';

type MoveNodeValidationReason =
	| 'sourceNotFound'
	| 'anchorNotFound'
	| 'sameNode'
	| 'invalidRelationship'
	| 'unsupportedTableStructure'
	| 'anchorInsideSource'
	| 'invalidSourceParent'
	| 'invalidDestination';

const doesNodeContain = ({ start, nodeSize }: { nodeSize: number; start: number }, pos: number) =>
	pos > start && pos < start + nodeSize;

const unsupportedTableStructureNodeTypes = new Set(['tableRow', 'tableCell', 'tableHeader']);
const isUnsupportedTableStructureNode = (nodeTypeName: string) =>
	unsupportedTableStructureNodeTypes.has(nodeTypeName);
const isValidRelationship = (
	relationship: MoveNodeCommand['relationship'] | string,
): relationship is MoveNodeCommand['relationship'] =>
	relationship === 'before' || relationship === 'after';

const getFragmentTypes = (node: PMNode) => node.content.content.map((child) => child.type.name);

const fireInvalidMoveNodeAnalytics = ({
	api,
	reason,
	sourceNodeType,
	anchorNodeType,
	sourceParentNodeType,
	destinationParentNodeType,
	parentNodeType,
	fragments,
}: {
	anchorNodeType?: string;
	api: Parameters<StreamingCommandFactory<MoveNodeCommand>>[0];
	destinationParentNodeType?: string;
	fragments?: string[];
	parentNodeType?: string;
	reason: MoveNodeValidationReason;
	sourceNodeType?: string;
	sourceParentNodeType?: string;
}) => {
	api?.analytics?.actions.fireAnalyticsEvent({
		action: ACTION.INVALID_COMMAND_FOUND,
		actionSubject: ACTION_SUBJECT.AI_STREAMING,
		actionSubjectId: ACTION_SUBJECT_ID.EXPERIENCE_APPLICATION,
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			success: false,
			repaired: false,
			errorMessage: 'moveNodeValidationFailed',
			validationReason: reason,
			sourceNodeType,
			anchorNodeType,
			sourceParentNodeType,
			destinationParentNodeType,
			parentNodeType,
			fragments,
		},
	});
};

export const createMoveNodeCommand: StreamingCommandFactory<MoveNodeCommand> =
	(api) => (command) => {
		if (!isExperimentEnabled('platform_editor_ai_move_node')) {
			return false;
		}

		return api.core.actions.execute(({ tr }) => {
			const reportMissingLocalId = (localId: string) => {
				// `findNodeByLocalId` owns the existing LOCAL_ID_NOT_FOUND analytics payload,
				// so we intentionally re-run it on miss even though we already did a pure lookup.
				findNodeByLocalId(api, tr, localId, () => {});
			};
			const nodeToMove = findNodeByLocalIdAndDoc(tr.doc, command.localId);
			if (!nodeToMove) {
				reportMissingLocalId(command.localId);
				fireInvalidMoveNodeAnalytics({ api, reason: 'sourceNotFound' });
				return null;
			}

			const anchorNode = findNodeByLocalIdAndDoc(tr.doc, command.anchorLocalId);
			if (!anchorNode) {
				reportMissingLocalId(command.anchorLocalId);
				fireInvalidMoveNodeAnalytics({
					api,
					reason: 'anchorNotFound',
					sourceNodeType: nodeToMove.node.type.name,
				});
				return null;
			}

			if (command.localId === command.anchorLocalId) {
				fireInvalidMoveNodeAnalytics({
					api,
					reason: 'sameNode',
					sourceNodeType: nodeToMove.node.type.name,
					anchorNodeType: anchorNode.node.type.name,
				});
				return null;
			}

			if (!isValidRelationship(command.relationship)) {
				fireInvalidMoveNodeAnalytics({
					api,
					reason: 'invalidRelationship',
					sourceNodeType: nodeToMove.node.type.name,
					anchorNodeType: anchorNode.node.type.name,
				});
				return null;
			}

			if (
				isUnsupportedTableStructureNode(nodeToMove.node.type.name) ||
				isUnsupportedTableStructureNode(anchorNode.node.type.name)
			) {
				fireInvalidMoveNodeAnalytics({
					api,
					reason: 'unsupportedTableStructure',
					sourceNodeType: nodeToMove.node.type.name,
					anchorNodeType: anchorNode.node.type.name,
				});
				return null;
			}

			if (
				doesNodeContain(
					{ start: nodeToMove.pos, nodeSize: nodeToMove.node.nodeSize },
					anchorNode.pos,
				)
			) {
				fireInvalidMoveNodeAnalytics({
					api,
					reason: 'anchorInsideSource',
					sourceNodeType: nodeToMove.node.type.name,
					anchorNodeType: anchorNode.node.type.name,
				});
				return null;
			}

			const $sourcePos = tr.doc.resolve(nodeToMove.pos);
			const sourceIndex = $sourcePos.index();
			if (!$sourcePos.parent.canReplace(sourceIndex, sourceIndex + 1, Fragment.empty)) {
				fireInvalidMoveNodeAnalytics({
					api,
					reason: 'invalidSourceParent',
					sourceNodeType: nodeToMove.node.type.name,
					anchorNodeType: anchorNode.node.type.name,
					sourceParentNodeType: $sourcePos.parent.type.name,
					parentNodeType: $sourcePos.parent.type.name,
				});
				return null;
			}

			const slice = tr.doc.slice(nodeToMove.pos, nodeToMove.pos + nodeToMove.node.nodeSize, false);
			const insertPos =
				command.relationship === 'before'
					? anchorNode.pos
					: anchorNode.pos + anchorNode.node.nodeSize;

			const validationTransform = new Transform(tr.doc);
			validationTransform.delete(nodeToMove.pos, nodeToMove.pos + nodeToMove.node.nodeSize);
			const mappedInsertPos = validationTransform.mapping.map(insertPos);
			const $mappedInsertPos = validationTransform.doc.resolve(mappedInsertPos);
			const insertIndex = $mappedInsertPos.index();
			if (!$mappedInsertPos.parent.canReplace(insertIndex, insertIndex, slice.content)) {
				fireInvalidMoveNodeAnalytics({
					api,
					reason: 'invalidDestination',
					sourceNodeType: nodeToMove.node.type.name,
					anchorNodeType: anchorNode.node.type.name,
					destinationParentNodeType: $mappedInsertPos.parent.type.name,
					parentNodeType: $mappedInsertPos.parent.type.name,
					fragments: getFragmentTypes(slice.content.firstChild ?? nodeToMove.node),
				});
				return null;
			}

			api.aiExperience?.actions.tagAsAIStreamingTransformation(tr, { isFinal: true });

			// [CCI-17297] Capture the moved node so we can record reviewable
			// segments. move is non-streamable: it mutates the doc directly here and
			// never reaches `buildCommitTransaction`. A move is modelled as a
			// "remove" at the source (zero-width after deletion) plus an "add" at the
			// destination. No-op when the New AIFC experience is OFF.
			const movedSlice = new Slice(Fragment.from(nodeToMove.node), 0, 0);
			const sourceFrom = tr.mapping.map(nodeToMove.pos);

			tr.delete(nodeToMove.pos, nodeToMove.pos + nodeToMove.node.nodeSize);
			const appliedInsertPos = tr.mapping.map(insertPos);
			tr.replace(appliedInsertPos, appliedInsertPos, slice);

			const collapsedSource = tr.mapping.map(sourceFrom);
			api.aiExperience?.actions.recordAIContentSegments(tr, [
				{
					startPos: collapsedSource,
					endPos: collapsedSource,
					originalUserContentSlice: movedSlice,
					newSlice: Slice.empty,
					kind: 'remove',
				},
				{
					startPos: appliedInsertPos,
					endPos: appliedInsertPos + slice.size,
					originalUserContentSlice: Slice.empty,
					newSlice: movedSlice,
					kind: 'add',
				},
			]);

			if (NodeSelection.isSelectable(nodeToMove.node)) {
				tr.setSelection(NodeSelection.create(tr.doc, appliedInsertPos));
			} else {
				tr.setSelection(TextSelection.near(tr.doc.resolve(appliedInsertPos)));
			}

			tr.scrollIntoView();
			api.aiExperience?.actions.stampAgentEditChrome(tr);
			return tr;
		});
	};
